const CACHE_TTL_OK   = 10 * 60 * 1000; // 10 min for a successfully decoded buffer
const CACHE_TTL_ERR  =      30 * 1000; // 30 s before retrying a key that failed
const PURGE_INTERVAL =  5 * 60 * 1000; // sweep for expired entries every 5 min

// Shared cache for decoded AudioBuffers and synthesis results.
// Pass one instance to all Sound constructors that should share the cache.
export default class SoundCache {
	#map = new Map();

	#intervalId = null;

	constructor() {
		this.#intervalId = setInterval(() => {
			const now = Date.now();
			for(const [key, entry] of this.#map) {
				if(now >= entry.expiresAt) this.#map.delete(key);
			}
		}, PURGE_INTERVAL);
	}

	destroy() {
		clearInterval(this.#intervalId);
		this.#map.clear();
	}

	// get(key, fn): returns cached value if fresh, else calls fn() to produce it.
	// - key=null/undefined → always calls fn() (no caching)
	// - AbortErrors are never cached (load was interrupted, not a real failure)
	async get(key, fn) {
		if(!key) return fn();
		const entry = this.#map.get(key);
		if(entry && Date.now() < entry.expiresAt) {
			if(entry.error) throw entry.error;
			return entry.value;
		}
		try {
			const value = await fn();
			this.#map.set(key, { value, expiresAt: Date.now() + CACHE_TTL_OK });
			return value;
		} catch(e) {
			if(!(e instanceof DOMException && e.name === 'AbortError'))
				this.#map.set(key, { error: e, expiresAt: Date.now() + CACHE_TTL_ERR });
			throw e;
		}
	}
}
